import Firefly from 'firefly-sdk-ts'
import merge from 'lodash/merge'
import InternalAppSchema from './InternalAppSchema'
import { api } from './config'
import { sanitizeUrl } from './utils'

class EventManager {
  /**
   *
   * @param eventManagerConfiguration
   * @example
   * const eventManager = EventManager({
   *   apiKey: '< Generate a new API key at http://go/developer/apps >',
   *   appName: '< your unique app name >',
   * })
   */
  constructor(
    eventManagerConfiguration: EventManagerConfiguration,
    loginId?: string,
  ) {
    if (
      !eventManagerConfiguration ||
      !(eventManagerConfiguration instanceof Object) ||
      !eventManagerConfiguration.apiKey ||
      !eventManagerConfiguration.appName
    ) {
      throw new Error(
        "[component-analytics] Parameter 'EventManagerConfiguration' was not provided or was of the wrong format. Properties 'apiKey' and 'appName' must contain a value.",
      )
    }
    const {
      apiKey,
      appName,
      eventManagerKey = 'internalappeventmanager',
      persistMethod = 'localStorage',
      retries = 1,
      schemaId,
      url = api.internal.stage,
    } = eventManagerConfiguration

    this.eventManager = Firefly.registerEventManagerWithConfig({
      apiKey,
      eventManagerKey,
      persistMethod,
      retries,
      schemaId,
      url,
    })
    this.appName = appName
    this.loginId = loginId

    Firefly.setGlobalDataLayer(
      this.setAppDataAtGlobalDataLayer(),
      this.eventManager.persistMethod,
    )
    this.eventManager.extractDataFromGlobalDataLayer =
      this.extractDataFromGlobalDataLayer
    this.eventManager.setAppDataAtGlobalDataLayer =
      this.setAppDataAtGlobalDataLayer
    this.eventManager.prepareDataLayer = this.prepareDataLayer
    return this.eventManager
  }
  private appName: string
  private loginId?: string
  private eventManager: EventManager

  // Not able to find a function to get data (json) at Global Data Layer
  // So, we utilize Firefly.appendToDataLayer which will append the given data object
  // and return back the final json present in the Global Data Layer
  extractDataFromGlobalDataLayer = (): InternalAppSchema => {
    return this.eventManager.appendToDataLayer({})
  }

  setAppDataAtGlobalDataLayer = (
    existing?: InternalAppSchema,
  ): InternalAppSchema => {
    // define our own values we set at the global level.
    const overrideState = {
      application: {
        appServer: window.location.host,
        name: this.appName,
        version: process.env.APP_VERSION,
      },
      browser: {
        // Firefly adds URLs by default, but there's no sanitization.
        // Overwrite them with values stripped of any query or hash parameters.
        // This may be heavy-handed, and adds limitations if our consumers want to
        // see their own query parameters, or if they use a HashRouter.
        referrer: sanitizeUrl(document.referrer),
        url: sanitizeUrl(window.location.href),
      },
      employee: {
        loginId: this.loginId,
      },
    }

    // merge our overrides with whatever may be passed in
    const mergeState = merge({}, existing, overrideState)
    const {
      appState, // appState is not a part of the public API so is not assignable but can contain sensitive url information
      ...rest
    } = mergeState
    return rest
  }

  prepareDataLayer = (): InternalAppSchema => {
    // Extract only the task data, from the Global Data Layer
    const {
      task, // preserve task if present
      customInteraction, // discard this so it doesn't persist between events. if it's new, it will come in via eventData
      customMetrics, // discard this so it doesn't persist between events. if it's new, it will come in via eventData
      ...rest // grab the rest to re-add by caller
    } = this.extractDataFromGlobalDataLayer()

    // Clear all the data user set before this event.
    this.eventManager.clearDataLayer()
    return { task: task, ...rest }
  }
}

interface BaseEventManagerConfiguration {
  /** Developer portal API key. Generate a new app and keys at http://go/developer/apps */
  apiKey: string

  /** Name of this event manager.
   * @default internalappeventmanager
   */
  eventManagerKey: string

  /** Method to persist data between sessions.
   * @default localStorage
   */
  persistMethod: 'localStorage' | 'cookies' | 'window'

  /** Number of times to retry a failed connection to Firefly Internal Insights.
   * @default 1
   */
  retries: number

  schemaId?: string

  /** URL to post event data to.
   * @default https://stgapi-internal.target.com/internal_app_analytics/v3
   */
  url:
    | 'https://stgapi-internal.target.com/internal_app_analytics/v3'
    | 'https://api-internal.target.com/internal_app_analytics/v3'
    | string
}

// Arguments used to create a new EventManager
export interface EventManagerConfiguration
  extends Partial<BaseEventManagerConfiguration> {
  /** Developer portal API key. Generate a new app and keys at http://go/developer/apps */
  apiKey: string

  /** Unique name of your application. */
  appName: string
  // Partial<> includes all values from the base configuration as optional
}

// eslint-disable-next-line no-redeclare
interface EventManager extends BaseEventManagerConfiguration {
  addEvent: (json: object) => void
  addTaskInitEvent: (json: object) => void
  addTaskSuccessEvent: (json: object) => void
  addTaskFailEvent: (json: object) => void
  addTaskCancelEvent: (json: object) => void
  appendToDataLayer: (json: object) => InternalAppSchema
  clearDataLayer: () => void
  setDataLayer: (json: object) => void
  prepareDataLayer: () => InternalAppSchema
  dataLayerInterceptor: DataLayerInterceptor
  eventInterceptor: EventInterceptor
}

// Unknown what these objects are, but they appear when console.log()ing out an EventManager
interface DataLayerInterceptor {}
interface EventInterceptor {}

export default EventManager
